import { useEffect, useState } from "react";
import { Download, Send } from "lucide-react";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import api, { formatError } from "../lib/api";
import { currentMonth, minutesLabel } from "../lib/utils";

export default function ReportsPage() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [month, setMonth] = useState(currentMonth());
  const [report, setReport] = useState(null);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/clients", { params: { status: "all" } }).then((response) => {
      setClients(response.data);
      setClientId(response.data[0]?.id || "");
    });
  }, []);

  useEffect(() => {
    if (!clientId) return;
    api.get("/reports/monthly", { params: { client_id: clientId, month } }).then((response) => setReport(response.data));
  }, [clientId, month]);

  const resend = async () => {
    setSending(true);
    try {
      await api.post("/reports/monthly/resend", null, { params: { client_id: clientId, month } });
      toast.success("Monthly summary sent to the client contacts.");
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSending(false);
    }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const response = await api.get("/reports/monthly/pdf", { params: { client_id: clientId, month }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `Intechify Monthly Support Summary - ${report.client.company_name} - ${month}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setDownloading(false);
    }
  };

  return <AppShell title="Reports" eyebrow="MONTHLY CLIENT SUMMARY"><section className="report-controls content-card" data-testid="report-controls"><label>Client<select value={clientId} onChange={(event) => setClientId(event.target.value)} data-testid="report-client-select">{clients.map((client) => <option key={client.id} value={client.id}>{client.company_name}</option>)}</select></label><label>Month<input type="month" value={month} onChange={(event) => setMonth(event.target.value)} data-testid="report-month-input" /></label><div className="report-action-buttons"><button className="secondary-button" disabled={!report?.entries?.length || downloading} onClick={download} data-testid="download-pdf-summary-button"><Download size={17} /> {downloading ? "Preparing…" : "Download PDF summary"}</button><button className="primary-button" disabled={!report?.entries?.length || sending} onClick={resend} data-testid="resend-monthly-summary-button"><Send size={17} /> {sending ? "Sending…" : "Resend monthly summary"}</button></div></section>{report && <section className="active-days-summary" data-testid="active-days-summary"><div className="active-day-stats"><strong>{report.entries.length} entries</strong><strong>{minutesLabel(report.total_minutes)}</strong><strong>{report.active_day_count} active days</strong></div><div className="active-day-bars">{report.active_days.map((day) => <div className="active-day-row" key={day.date}><span>{day.date}</span><i style={{ width: `${Math.max(8, day.minutes / Math.max(...report.active_days.map((item) => item.minutes)) * 100)}%` }} /><em>{minutesLabel(day.minutes)} · {day.entries} {day.entries === 1 ? "entry" : "entries"}</em></div>)}</div></section>}{report && <section className="report-summary" data-testid="monthly-report"><div><p className="eyebrow">{report.month}</p><h2>{report.client.company_name}</h2><p>Support delivered across {report.entries.length} logged entries.</p></div><strong data-testid="report-total-duration">{minutesLabel(report.total_minutes)}</strong></section>}<section className="content-card table-card"><div className="table-wrap"><table><thead><tr><th>Date</th><th>Service type</th><th>Support Engineer</th><th>User / device</th><th>Duration</th><th>Description</th></tr></thead><tbody>{report?.entries?.map((entry) => <tr key={entry.id} data-testid={`report-entry-${entry.id}`}><td>{entry.date}</td><td><span className="type-pill">{entry.support_type}</span></td><td>{entry.technician_name}</td><td>{entry.supported_user_device}</td><td><strong>{entry.duration_label}</strong></td><td>{entry.description}</td></tr>)}</tbody></table>{report?.entries?.length === 0 && <p className="empty-state" data-testid="report-empty-state">No support entries were logged for this client in this month.</p>}</div></section></AppShell>;
}